import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatCurrency } from '../../lib/format.js';
import { useCashFlowRows } from '../../hooks/useCashFlowRows.js';
import { useCashFlowLayout } from '../../hooks/useCashFlowLayout.js';
import { computeSubtotals } from '../../lib/cashflow-subtotals.js';
import {
  buildCellDetail,
  buildUnclassifiedDetail,
  extractVatSeries,
  formatXLabel,
} from '../../lib/cashflow-tree.js';
import { actualColorClass, forecastColorClass } from './CellPair.jsx';

function defaultBucket(buckets, today) {
  return (
    buckets.find((b) => b.period_start <= today && today <= b.period_end) ??
    [...buckets].reverse().find((b) => !b.is_future) ??
    buckets[0]
  );
}

function chipLabel(col) {
  const start = new Date(col.period_start + 'T00:00:00');
  const end = new Date(col.period_end + 'T00:00:00');
  const spansMonth = (end - start) / 86400000 >= 27;
  if (spansMonth) return formatXLabel(col.period_start);
  return start.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function pickAmounts(pp) {
  return {
    actual:   pp?.actual_ht_cents   ?? pp?.actual_cents   ?? null,
    forecast: pp?.forecast_ht_cents ?? pp?.forecast_cents ?? null,
    budget:   pp?.budget_cents ?? null,
  };
}

function ValueStack({ actual, second, secondLabel, flowType, isFuture, showActual, showSecond, strong = false }) {
  return (
    <span className="shrink-0 w-28 text-right tabular-nums leading-tight">
      {showActual && (
        <span
          className={`block text-sm ${strong ? 'font-semibold' : 'font-medium'} ${isFuture ? 'text-slate-300' : actualColorClass(actual, flowType)}`}
          title="Réel"
        >
          {isFuture || actual == null ? '—' : formatCurrency(actual)}
        </span>
      )}
      {showSecond && (
        <span
          className={`block text-xs italic ${secondLabel === 'Budget' ? 'text-slate-500' : forecastColorClass(second, flowType)}`}
          title={secondLabel}
        >
          {second != null ? formatCurrency(second) : '—'}
        </span>
      )}
    </span>
  );
}

function CategoryRow({ node, level, col, onToggle, onOpen, viewMode, expandedIds }) {
  const hasChildren = node.children.length > 0;
  const isOpen = expandedIds.has(node.id);
  const pp = node.per_period.find((p) => p.period_start === col.period_start);
  const { actual, forecast, budget } = pickAmounts(pp);
  const showBudget = viewMode === 'budget';

  return (
    <>
      <li className={`flex items-stretch min-h-12 border-b border-slate-100 ${level === 0 ? 'bg-white' : level === 1 ? 'bg-slate-50' : 'bg-white'}`}>
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            aria-label={isOpen ? `Replier ${node.name}` : `Déplier ${node.name}`}
            aria-expanded={isOpen}
            className="shrink-0 w-10 flex items-center justify-center text-slate-400"
            style={{ marginLeft: `${level * 14}px` }}
          >
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : (
          <span className="shrink-0 w-10" style={{ marginLeft: `${level * 14}px` }} />
        )}
        <button
          type="button"
          onClick={() => onOpen(node, col)}
          className="flex-1 min-w-0 flex items-center justify-between gap-2 py-2 pr-4 text-left"
        >
          <span className={`min-w-0 line-clamp-2 break-words text-sm ${level === 0 ? 'font-medium text-slate-800' : 'text-slate-700'}`}>
            {node.name}
          </span>
          <ValueStack
            actual={actual}
            second={showBudget ? budget : forecast}
            secondLabel={showBudget ? 'Budget' : 'Prévi'}
            flowType={node.flow_type}
            isFuture={col.is_future}
            showActual={viewMode !== 'forecast'}
            showSecond={viewMode !== 'actual'}
          />
        </button>
      </li>
      {isOpen &&
        node.children.map((child) => (
          <CategoryRow
            key={child.id}
            node={child}
            level={Math.min(level + 1, 2)}
            col={col}
            onToggle={onToggle}
            onOpen={onOpen}
            viewMode={viewMode}
            expandedIds={expandedIds}
          />
        ))}
    </>
  );
}

function SummaryLine({ label, bucketPart, col, viewMode, flowType, field }) {
  const actual = bucketPart?.actual?.[field] ?? null;
  const forecast = bucketPart?.forecast?.[field] ?? null;
  const budget = bucketPart?.budget?.[field] ?? null;
  const showBudget = viewMode === 'budget';
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <span className="text-sm text-slate-600">{label}</span>
      <ValueStack
        actual={actual}
        second={showBudget ? budget : forecast}
        secondLabel={showBudget ? 'Budget' : 'Prévi'}
        flowType={flowType}
        isFuture={col.is_future}
        showActual={viewMode !== 'forecast'}
        showSecond={viewMode !== 'actual'}
      />
    </div>
  );
}

/**
 * Vue mobile portrait du cash-flow : un mois à la fois.
 *
 * Props : mêmes données que ActualVsBudgetTable ; `viewMode` vient de la page.
 *   onCellClick / onVatCellClick : mêmes handlers que le tableau (drill-down).
 */
export default function CashFlowMonthView({
  byCategory = [],
  buckets = [],
  viewMode = 'all',
  today,
  onCellClick,
  onVatCellClick,
  cashFlowData,
}) {
  const { cols, periodStarts, bucketByPeriod, tree, nodeById } = useCashFlowRows({ byCategory, buckets });
  const { data: savedLayout } = useCashFlowLayout();

  const todayISO = today ?? new Date().toISOString().slice(0, 10);
  const [selectedStart, setSelectedStart] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const selectedChipRef = useRef(null);

  const selectedBucket =
    buckets.find((b) => b.period_start === selectedStart) ?? defaultBucket(buckets, todayISO);
  const col = cols.find((c) => c.period_start === selectedBucket?.period_start);

  useEffect(() => {
    // Scroll the chip strip only: scrollIntoView would also scroll the page on open.
    const chip = selectedChipRef.current;
    const strip = chip?.closest('[role="tablist"]');
    if (strip) strip.scrollLeft = chip.offsetLeft - (strip.clientWidth - chip.offsetWidth) / 2;
  }, [col?.period_start]);

  const layout = Array.isArray(savedLayout) ? savedLayout : [];
  const hasLayout = layout.length > 0;

  const subtotalData = useMemo(
    () => (hasLayout ? computeSubtotals(layout, nodeById, periodStarts) : new Map()),
    [layout, hasLayout, nodeById, periodStarts]
  );

  const unplacedNodes = useMemo(() => {
    if (!hasLayout) return [];
    const placed = new Set(layout.filter((r) => r.kind === 'category').map((r) => r.category_id));
    return tree.filter((r) => !placed.has(r.id));
  }, [hasLayout, layout, tree]);

  if (!buckets.length || !col) return null;

  function toggle(id) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function openDetail(node, c) {
    onCellClick?.(buildCellDetail(node, c));
  }

  const showActual = viewMode !== 'forecast';
  const showSecond = viewMode !== 'actual';
  const secondLabel = viewMode === 'budget' ? 'Budget' : 'Prévi';
  const bucket = selectedBucket;
  const isMonth = (new Date(col.period_end) - new Date(col.period_start)) / 86400000 >= 27;
  const monthIndex = cols.findIndex((c) => c.period_start === col.period_start);

  const rowProps = { col, onToggle: toggle, onOpen: openDetail, viewMode, expandedIds };

  const unclassified = bucket.unclassified_actual_cents ?? 0;
  const vat = extractVatSeries(cashFlowData?.vat_summary);
  const vatRows = vat
    ? [
        vat.hasCollected && {
          key: 'collected', label: 'TVA collectée', direction: 'collected', tone: 'text-green-700',
          actual: vat.collectedActual[monthIndex] ?? 0, forecast: vat.collectedForecast[monthIndex] ?? 0,
        },
        vat.hasDeductible && {
          key: 'deductible', label: 'TVA déductible', direction: 'deductible', tone: 'text-red-600',
          actual: vat.deductibleActual[monthIndex] ?? 0, forecast: vat.deductibleForecast[monthIndex] ?? 0,
        },
      ].filter(Boolean)
    : [];

  return (
    <div className="space-y-3">
      <div
        className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1"
        role="tablist"
        aria-label="Choisir la période"
      >
        {cols.map((c) => {
          const active = c.period_start === col.period_start;
          return (
            <button
              key={c.period_start}
              ref={active ? selectedChipRef : undefined}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setSelectedStart(c.period_start)}
              className={`snap-center shrink-0 min-h-10 px-4 rounded-full border text-sm font-medium capitalize transition-colors ${
                active
                  ? 'bg-slate-800 border-slate-800 text-white'
                  : c.is_future
                  ? 'bg-slate-50 border-slate-200 text-slate-600'
                  : 'bg-white border-slate-200 text-slate-700'
              }`}
            >
              {chipLabel(c)}
            </button>
          );
        })}
      </div>

      <section className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3" aria-label="Résumé de la période">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
          {isMonth ? 'Solde fin de mois' : 'Solde fin de période'}
        </p>
        <p className="text-2xl font-bold tabular-nums text-slate-900" data-testid="month-balance">
          {bucket.balance_end_cents != null ? formatCurrency(bucket.balance_end_cents) : '—'}
        </p>
        <div className="mt-2 divide-y divide-slate-100 border-t border-slate-100">
          <div className="flex justify-end gap-6 pt-2 text-[11px] text-slate-400">
            {showActual && <span>Réel</span>}
            {showSecond && <span className="italic">{secondLabel}</span>}
          </div>
          <SummaryLine label="Encaissements" bucketPart={bucket} col={col} viewMode={viewMode} flowType="income" field="income_cents" />
          <SummaryLine label="Décaissements" bucketPart={bucket} col={col} viewMode={viewMode} flowType="expense" field="expense_cents" />
        </div>
      </section>

      <ul className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" aria-label="Détail par catégorie">
        {hasLayout
          ? layout.map((row) => {
              if (row.kind === 'category') {
                const node = nodeById.get(row.category_id);
                return node ? <CategoryRow key={row.id} node={node} level={0} {...rowProps} /> : null;
              }
              if (row.kind === 'subtotal') {
                const pp = (subtotalData.get(row.id) ?? []).find((p) => p.period_start === col.period_start);
                const { actual, forecast } = pickAmounts(pp);
                return (
                  <li key={row.id} className="flex items-center justify-between gap-2 min-h-12 px-4 py-2 bg-slate-100 border-b border-slate-200">
                    <span className="min-w-0 line-clamp-2 break-words text-sm font-semibold text-slate-800">
                      {row.label || 'Solde intermédiaire'}
                    </span>
                    <ValueStack
                      actual={actual}
                      second={forecast}
                      secondLabel="Prévi"
                      flowType="neutral"
                      isFuture={col.is_future}
                      showActual={showActual}
                      showSecond={viewMode !== 'actual' && viewMode !== 'budget'}
                      strong
                    />
                  </li>
                );
              }
              return null;
            })
          : tree.map((root) => <CategoryRow key={root.id} node={root} level={0} {...rowProps} />)}
        {unplacedNodes.map((node) => (
          <CategoryRow key={node.id} node={node} level={0} {...rowProps} />
        ))}

        {unclassified !== 0 && (
          <li className="bg-amber-50 border-t border-slate-200">
            <button
              type="button"
              disabled={col.is_future || !onCellClick}
              onClick={() => onCellClick?.(buildUnclassifiedDetail(col))}
              className="w-full min-h-12 px-4 py-2 flex items-center justify-between gap-2 text-left"
            >
              <span className="text-sm italic text-slate-600">Transactions à catégoriser</span>
              <span className="text-sm font-medium tabular-nums text-orange-600">{formatCurrency(unclassified)}</span>
            </button>
          </li>
        )}

        {vatRows.map((v) => (
          <li key={v.key} className="border-t border-slate-100 bg-slate-50/60">
            <button
              type="button"
              disabled={!onVatCellClick || (v.actual === 0 && v.forecast === 0)}
              onClick={() => onVatCellClick?.(col, v.direction, v.actual !== 0 ? 'actuals' : 'forecasts')}
              className="w-full min-h-12 px-4 py-2 flex items-center justify-between gap-2 text-left"
            >
              <span className={`text-sm italic ${v.tone}`}>{v.label}</span>
              <ValueStack
                actual={v.actual}
                second={v.forecast}
                secondLabel="Prévi"
                flowType={v.direction === 'collected' ? 'income' : 'expense'}
                isFuture={col.is_future}
                showActual={showActual}
                showSecond={viewMode !== 'actual' && viewMode !== 'budget'}
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
